// Componente EditarPerfil para usuarios autenticados
// Permite actualizar información personal, bio, fotos y otros campos del perfil
// Migrado desde la app móvil (UserProfileDrawer.js)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiX } from 'react-icons/fi';
import { ages, alturas, kilitos, femEtnia, homEtnia, femFisic, homFisic, femRol, homRol, femSituAmorosa, homSituAmorosa } from '../../constants/humanQualities';
import { useUserProfileQuery, useUpdateUserProfileMutation } from '../../hooks/useUserProfile';
import MegaButton from '../../components/UI/MegaButton';
import AlbumManager from '../../components/album-sistem/my-albums/AlbumManager';
import toast from 'react-hot-toast';

/**
 * Componente EditarPerfil
 * 
 * Vista para editar el perfil del usuario
 * Permite actualizar información personal, bio, fotos y otros campos
 * Incluye gestión de fotos de perfil con sistema de álbumes
 */
const EditarPerfil = () => {
  const navigate = useNavigate();

  // Hook para obtener perfil actual
  const { data: userProfile, isLoading: loadingProfile } = useUserProfileQuery({
    enabled: true,
  });

  // Hook para guardar perfil
  const updateProfileMutation = useUpdateUserProfileMutation();

  // Estado para la selección de imágenes del álbum
  const [profileSelection, setProfileSelection] = useState([]);

  // Estado local para el formulario
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [altura_index, set_altura_index] = useState(null);
  const [bio_index, set_bio_index] = useState('');
  const [full_name_index, set_full_name_index] = useState('');
  const [origenetnico_index, set_origenetnico_index] = useState(null);
  const [peso_index, set_peso_index] = useState(null);
  const [rol_index, set_rol_index] = useState(null);
  const [sex_index, set_sex_index] = useState(null);
  const [situacionamorosa_index, set_situacionamorosa_index] = useState(null);
  const [years_index, set_years_index] = useState(null);
  const [complexion_index, set_complexion_index] = useState(null);
  const [instagram_index, set_instagram_index] = useState(null);
  const [twitter_index, set_twitter_index] = useState(null);
  const [place_status, set_place_status] = useState(null);

  // Referencia para controlar inicialización
  const isInitializing = useRef(true);

  // Efecto para cargar datos iniciales
  useEffect(() => {
    if (userProfile && isInitializing.current) {
      set_full_name_index(userProfile.full_name || '');
      set_bio_index(userProfile.bio || '');
      set_peso_index(userProfile.peso);
      set_altura_index(userProfile.altura);
      set_rol_index(userProfile.rol);
      set_sex_index(userProfile.sex);
      set_years_index(userProfile.years);
      set_complexion_index(userProfile.complexion);
      set_origenetnico_index(userProfile.origenetnico);
      set_situacionamorosa_index(userProfile.situacionamorosa);
      set_place_status(userProfile.place_status);
      set_instagram_index(userProfile.instagram || '');
      set_twitter_index(userProfile.twitter || '');
      setIsFormChanged(false);
      isInitializing.current = false;
    }
  }, [userProfile]);

  // Actualiza estado de cambios en formulario
  const handleFormChange = useCallback(() => {
    if (!isInitializing.current) {
      setIsFormChanged(true);
    }
  }, []);

  // Guardar cambios
  const saveMyData = useCallback(async () => {
    const miNuevaData = {
      altura: altura_index,
      bio: bio_index,
      full_name: full_name_index,
      origenetnico: origenetnico_index,
      peso: peso_index,
      rol: rol_index,
      sex: sex_index,
      situacionamorosa: situacionamorosa_index,
      years: years_index,
      complexion: complexion_index,
      instagram: instagram_index,
      twitter: twitter_index,
      place_status: place_status,
    };

    try {
      await updateProfileMutation.mutateAsync(miNuevaData);
      toast.success('Perfil actualizado correctamente');
      setIsFormChanged(false);
      isInitializing.current = true;
      navigate('/mi-cuenta');
    } catch (error) {
      toast.error('No se pudo actualizar el perfil');
    }
  }, [altura_index, bio_index, full_name_index, origenetnico_index, peso_index, rol_index, sex_index, situacionamorosa_index, years_index, complexion_index, instagram_index, twitter_index, place_status, updateProfileMutation, navigate]);

  // Confirmar descartar cambios
  const handleDiscardChanges = useCallback(() => {
    if (window.confirm('¿Descartar los cambios? Los cambios en tu perfil NO se guardarán.')) {
      if (userProfile) {
        set_full_name_index(userProfile.full_name || '');
        set_bio_index(userProfile.bio || '');
        set_peso_index(userProfile.peso);
        set_altura_index(userProfile.altura);
        set_rol_index(userProfile.rol);
        set_sex_index(userProfile.sex);
        set_years_index(userProfile.years);
        set_complexion_index(userProfile.complexion);
        set_origenetnico_index(userProfile.origenetnico);
        set_situacionamorosa_index(userProfile.situacionamorosa);
        set_place_status(userProfile.place_status);
        set_instagram_index(userProfile.instagram || '');
        set_twitter_index(userProfile.twitter || '');
      }
      setIsFormChanged(false);
      isInitializing.current = true;
      navigate('/mi-cuenta');
    }
  }, [userProfile, navigate]);

  // Manejar cierre de la página
  const handleClose = useCallback(() => {
    if (isFormChanged) {
      handleDiscardChanges();
    } else {
      isInitializing.current = true;
      navigate('/mi-cuenta');
    }
  }, [isFormChanged, navigate, handleDiscardChanges]);

  if (loadingProfile) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-600 dark:text-neutral-400">Cargando perfil...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="h-20 px-2 gap-2 flex items-center bg-neutral-900 shadow-sm border-b border-neutral-800">
        <button
          onClick={handleClose}
          className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
        >
          <FiArrowLeft size={24} color="white" />
        </button>
        <h2 className="text-2xl font-bold text-white">
          Editar perfil
        </h2>
      </div>

      <div className="overflow-y-auto max-h-[calc(100dvh-5rem)]">

        <div className="p-4 space-y-6">
          {userProfile?.id ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div className='space-y-6'>
                {/* Nombre de pantalla */}
                <div className="bg-neutral-800 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-lg font-medium text-white">
                      Nombre de pantalla
                    </label>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">
                      {full_name_index.length}/16
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Todos verán esto en la cuadrícula"
                    value={full_name_index}
                    maxLength={16}
                    onChange={(e) => { set_full_name_index(e.target.value); setIsFormChanged(true); }}
                  />
                </div>

                {/* Acerca de mí */}
                <div className="bg-neutral-800 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-lg font-medium text-white">
                      Acerca de mí
                    </label>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">
                      {bio_index.length}/250
                    </span>
                  </div>
                  <textarea
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 h-32 resize-none"
                    placeholder="Lo que la gente debería saber de ti..."
                    value={bio_index}
                    maxLength={250}
                    onChange={(e) => { set_bio_index(e.target.value); setIsFormChanged(true); }}
                  />
                </div>

                {/* Fotos de perfil */}
                <div className="bg-neutral-800 rounded-lg p-4">
                  <AlbumManager
                    sectionName="Fotos de Perfil"
                    album="fotosDePerfil"
                    maxLimit={6}
                    selectionList={profileSelection}
                    setSelectionList={setProfileSelection}
                    onDeleteComplete={() => setProfileSelection([])}
                    indiscreto={true}
                    hideDeleteButton={false}
                    hideUploadButton={false}
                  />
                </div>
              </div>

              <div className='space-y-6'>
                {/* Identificación de género */}
                <div className="bg-neutral-800 rounded-lg p-4">
                  <label className="text-lg font-medium text-white mb-4 block">
                    Me identifico como:
                  </label>
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => { set_sex_index(1); handleFormChange(); }}
                      className={`flex-1 p-1 rounded-full border-2 transition-all ${
                        sex_index === 1
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-neutral-900 border-neutral-700 text-white hover:border-primary-300'
                      }`}
                    >
                      Mujer
                    </button>
                    <button
                      type="button"
                      onClick={() => { set_sex_index(2); handleFormChange(); }}
                      className={`flex-1 p-1 rounded-full border-2 transition-all ${
                        sex_index === 2
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-neutral-900 border-neutral-700 text-white hover:border-primary-300'
                      }`}
                    >
                      Hombre
                    </button>
                  </div>
                </div>

                {/* Selectores para otros campos */}
                <div className="bg-neutral-800 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Edad */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Edad
                      </label>
                      <select
                        value={years_index || ''}
                        onChange={(e) => { set_years_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar edad</option>
                        {ages.map((age) => (
                          <option key={age} value={age}>
                            {age} Años
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Altura */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Altura
                      </label>
                      <select
                        value={altura_index || ''}
                        onChange={(e) => { set_altura_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar altura</option>
                        {alturas.map((altura) => (
                          <option key={altura} value={altura}>
                            {altura} Cm
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Peso */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Peso
                      </label>
                      <select
                        value={peso_index || ''}
                        onChange={(e) => { set_peso_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar peso</option>
                        {kilitos.map((peso) => (
                          <option key={peso} value={peso}>
                            {peso} Kg
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Origen étnico */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Origen étnico
                      </label>
                      <select
                        value={origenetnico_index !== null ? origenetnico_index : ''}
                        onChange={(e) => { set_origenetnico_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar origen étnico</option>
                        {(sex_index === 1 ? femEtnia : homEtnia).map((etnia, index) => (
                          <option key={index} value={index}>
                            {etnia}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Complexión física */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Complexión física
                      </label>
                      <select
                        value={complexion_index !== null ? complexion_index : ''}
                        onChange={(e) => { set_complexion_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar complexión</option>
                        {(sex_index === 1 ? femFisic : homFisic).map((fisic, index) => (
                          <option key={index} value={index}>
                            {fisic}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Rol */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Rol
                      </label>
                      <select
                        value={rol_index !== null ? rol_index : ''}
                        onChange={(e) => { set_rol_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar rol</option>
                        {(sex_index === 1 ? femRol : homRol).map((rol, index) => (
                          <option key={index} value={index}>
                            {rol}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Situación amorosa */}
                    <div>
                      <label className="text-lg font-medium text-white mb-2 block">
                        Situación amorosa
                      </label>
                      <select
                        value={situacionamorosa_index !== null ? situacionamorosa_index : ''}
                        onChange={(e) => { set_situacionamorosa_index(Number(e.target.value)); handleFormChange(); }}
                        className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Seleccionar situación</option>
                        {(sex_index === 1 ? femSituAmorosa : homSituAmorosa).map((situacion, index) => (
                          <option key={index} value={index}>
                            {situacion}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Disponibilidad */}
                    <div>
                      <label className="text-lg font-medium text-white mb-4 block">
                        Disponibilidad:
                      </label>
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => { set_place_status(0); handleFormChange(); }}
                          className={`flex-1 p-1 rounded-full border-2 transition-all ${
                            place_status === 0
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'bg-neutral-900 border-neutral-700 text-white hover:border-primary-300'
                          }`}
                        >
                          Sin Lugar
                        </button>
                        <button
                          type="button"
                          onClick={() => { set_place_status(1); handleFormChange(); }}
                          className={`flex-1 p-1 rounded-full border-2 transition-all ${
                            place_status === 1
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'bg-neutral-900 border-neutral-700 text-white hover:border-primary-300'
                          }`}
                        >
                          Con Lugar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Instagram */}
                {/* <div className="bg-neutral-800 rounded-lg p-4">
                  <label className="text-lg font-medium text-white mb-2 block">
                    Instagram
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Agrega tu @ de Instagram"
                    value={instagram_index}
                    maxLength={16}
                    onChange={(e) => { set_instagram_index(e.target.value); setIsFormChanged(true); }}
                  />
                </div> */}

                {/* Twitter */}
                {/* <div className="bg-neutral-800 rounded-lg p-4">
                  <label className="text-lg font-medium text-white mb-2 block">
                    Twitter
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Agrega tu @ de Twitter"
                    value={twitter_index}
                    maxLength={16}
                    onChange={(e) => { set_twitter_index(e.target.value); setIsFormChanged(true); }}
                  />
                </div> */}

              {/* Botones de acción */}
              <div className="px-4 pb-4">
                <div className="flex items-center justify-end gap-4">
                  <MegaButton
                    onPress={handleClose}
                    labelText="Cancelar"
                    className="!bg-neutral-800 text-white hover:bg-neutral-300 dark:hover:bg-neutral-600"
                    disabled={updateProfileMutation.isPending}
                  />
                  <MegaButton
                    onPress={saveMyData}
                    labelText="Guardar cambios"
                    loading={updateProfileMutation.isPending}
                    className={!isFormChanged 
                        ? '!bg-primary-500 text-white hover:bg-neutral-300 dark:hover:bg-neutral-600' 
                        : '!bg-primary-700 text-white hover:bg-neutral-300 dark:hover:bg-neutral-600'}
                    disabled={!isFormChanged}
                  />
                </div>
              </div>

              </div>

            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-neutral-600 dark:text-neutral-400">Cargando perfil...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditarPerfil;
